<template>
    <div class="py-12">
        <inertia-link :href="route('kiosk.changelog.index')">
            <AtomicButton color="yellow" size="sm">
                {{ $t("vue.back_to_changelog") }}
                <template #icon>
                    <BackspaceIcon
                        class="mr-2 h-6 w-6 transition duration-200 group-hover:text-gray-600"
                    />
                </template>
            </AtomicButton>
        </inertia-link>

        <h1
            class="mb-6 mt-2 text-2xl font-medium leading-normal tracking-small text-gray-800"
        >
            {{ $t("vue.edit") }}
        </h1>

        <ChangelogEntryForm
            :entry="entry"
            :submit-label="$t('vue.save_changes')"
            :processing="loading"
            :errors="errors"
            @submit="update"
        >
            <template #actions>
                <AtomicButton
                    size="sm"
                    color="red"
                    @click.prevent="deleteEntry"
                >
                    {{ $t("vue.delete") }}
                    <template #icon>
                        <TrashIcon class="mr-2 h-6 w-6" />
                    </template>
                </AtomicButton>
            </template>
        </ChangelogEntryForm>
    </div>
</template>

<script lang="ts" setup>
import { ref } from "vue";
import { BackspaceIcon, TrashIcon } from "@heroicons/vue/24/outline";
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import ChangelogEntryForm from "@/Pages/Kiosk/Changelog/components/ChangelogEntryForm.vue";
import type IChangelogEntry from "@/types/Changelog/IChangelogEntry";
import type IInputErrors from "@/types/Atomic/IInputErrors";
import { create } from "@/composables/useNotifications";
import useConfirm from "@/composables/useConfirm";

const props = defineProps<{
    entry: IChangelogEntry;
}>();

const { show } = useConfirm();

const loading = ref(false);
const errors = ref<IInputErrors>({});

const update = (payload: Record<string, unknown>) => {
    loading.value = true;
    router.put(
        route("kiosk.changelog.update", { changelogEntry: props.entry.id }),
        payload,
        {
            preserveScroll: true,
            onError: (err) => {
                errors.value = err;
            },
            onSuccess: () => {
                errors.value = {};
            },
            onFinish: () => {
                loading.value = false;
            },
        },
    );
};

const deleteEntry = async () => {
    const confirmed = await show({
        header: $t("vue.delete"),
        message: $t("vue.delete_changelog_entry_message"),
        type: "danger",
        confirm: $t("vue.delete"),
    });

    if (confirmed) {
        router.delete(
            route("kiosk.changelog.destroy", {
                changelogEntry: props.entry.id,
            }),
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: $t("vue.changelog_entry_deleted"),
                        type: "success",
                    });
                },
                onError: () => {
                    create({
                        title: $t("vue.changelog_entry_not_deleted"),
                        type: "error",
                    });
                },
            },
        );
    }
};
</script>
